/**
 * Lobbing the ball over a goalkeeper, and working out whether it would actually go in.
 *
 * A CHIP CANNOT BE A SHOT IN THIS ENGINE. `shotVector` throws away the height of whatever
 * direction it is handed and re-sets it to 0.05 — about three degrees — so nothing that goes
 * through the shot contact can ever leave the ground (`src/sim/anim/touchvector.ts`). A pass
 * keeps the lift it is given. So a chip is a HIGH PASS aimed at the goal, launched steeper than
 * an ordinary cross and with its power solved for the range rather than read off a key.
 *
 * THE TEXTBOOK RANGE FORMULA IS NO USE HERE, and using it drops the ball on the keeper's head.
 * The ball model applies quadratic air drag of 0.015 (`src/core/constants.ts`), which at 15 m/s
 * is 3.4 m/s² — a third of gravity again. A vacuum solution for a twenty-metre chip therefore
 * falls several metres short. So the flight is integrated here with the same two lines the ball
 * itself steps, in the same order and at the same 10 ms: gravity onto the vertical speed, then
 * drag onto the whole speed (`src/sim/ball.ts`). Everything below those two in the ball's own
 * step — the bounce, the grass, the woodwork, the net — only bites once the ball is down, which
 * for a chip is past the goal line. Started clear of the grass, this agrees with a real ball to
 * the centimetre at every distance, which a test pins.
 *
 * IT LANDS ABOUT THREE PER CENT SHORT OFF THE DECK, and that is one tick rather than an error. A
 * ball struck off the turf spends its first ten milliseconds inside the grass, where the ball
 * model takes ground friction off it and hands some of the momentum to spin. Modelling that here
 * would be false precision — the contact's own difficulty moves the ball several times as far —
 * so it is left alone and written down: a chip solved to land two metres past the line lands
 * about one metre past it, still in the goal and still coming down. Also pinned.
 *
 * IT IS AN AIM, NOT A PROMISE. What comes back from here is the direction and the power handed
 * to the pass contact, and the contact then adds its own difficulty, blends the direction
 * towards the one the clip was drawn with and adds height for both. A chip that is solved
 * perfectly still misses sometimes, which is the whole of what makes it worth trying.
 */

import { Vector3 } from 'three';
import { BALL, TICK_MS } from '../core/constants';

/**
 * The launch angle. Steep enough that the ball is above a keeper's hands by the time it reaches
 * him and shallow enough that it is still travelling: at 20 m it leaves the boot at 15.7 m/s and
 * is airborne for 1.7 s, which is about as long as a chip can hang before a defender heads it
 * away. Chosen rather than derived, and the one number here that taste settled.
 */
export const CHIP_ANGLE = (35 * Math.PI) / 180;

/**
 * How far past the goal line the chip is solved to land.
 *
 * Solving for the line itself would have the ball arriving at ground level, which is a shot. Two
 * metres past it puts the crossing height at `1.4 x distance / (distance + 2)` in a vacuum —
 * about 1.3 m from 20 m out and never above 1.4 m however far away he is, so it is always well
 * under a 2.44 m crossbar and always still coming down. Air drag steepens the descent, so the
 * real figure is lower again.
 */
export const CHIP_OVERSHOOT_M = 2;

/**
 * How high a standing man reaches with his arms up, as a multiple of his own height.
 *
 * The anthropometric figure, and the honest one for this act: a keeper going backwards under a
 * dropping ball is not jumping. It is asked of the keeper himself rather than being a constant,
 * for the same reason the header ceiling is — a 1.85 m keeper is beatable by a ball a 1.95 m one
 * catches.
 */
export const STANDING_REACH_RATIO = 1.35;

/** The slowest and fastest launch the solver will consider, in metres a second. */
const MIN_SPEED = 6;
const MAX_SPEED = 40;

/** Six seconds. A chip is airborne for under two; this is only a runaway guard. */
const MAX_STEPS = 600;

const TIME_STEP = TICK_MS / 1000;

export interface ChipFlight {
  /** How fast the ball must leave the boot, in metres a second. */
  speed: number;
  /** How far downfield it lands, in metres. */
  range: number;
  /** How high it is once it has run `x` metres downfield, in metres. */
  heightAt(x: number): number;
  /** How long it has been in the air by the time it has run `x` metres, in seconds. */
  timeAt(x: number): number;
}

/**
 * Fly a ball launched at `speed` and `CHIP_ANGLE` until it comes back down, recording where it
 * was. Positions are appended to the two arrays handed in, which are reused between calls.
 */
function fly(speed: number, xs: number[], zs: number[]): number {
  let x = 0;
  let z = BALL.radius;
  let vx = speed * Math.cos(CHIP_ANGLE);
  let vz = speed * Math.sin(CHIP_ANGLE);
  xs[0] = x;
  zs[0] = z;
  let count = 1;

  for (let step = 0; step < MAX_STEPS; step++) {
    // The ball's own two lines, in the ball's own order: gravity onto the vertical speed first,
    // then drag onto the whole speed, then the position from the speed that came out.
    vz += BALL.gravity * TIME_STEP;
    const current = Math.hypot(vx, vz);
    if (current > 1e-9) {
      const scale = (current - BALL.drag * current * current * TIME_STEP) / current;
      vx *= scale;
      vz *= scale;
    }
    x += vx * TIME_STEP;
    z += vz * TIME_STEP;
    xs[count] = x;
    zs[count] = z;
    count++;
    if (z <= BALL.radius && vz < 0) break;
  }
  return count;
}

const _xs: number[] = [];
const _zs: number[] = [];

/** Where a ball launched at this speed goes. */
export function chipFlight(speed: number): ChipFlight {
  const count = fly(speed, _xs, _zs);
  // Copied out, because the next solve overwrites the scratch arrays underneath this one.
  const xs = _xs.slice(0, count);
  const zs = _zs.slice(0, count);
  /** Where `x` falls in the recorded flight: the step before it, and how far past it. */
  function locate(x: number): { step: number; fraction: number } {
    let low = 0;
    let high = count - 1;
    while (high - low > 1) {
      const mid = (low + high) >> 1;
      if (xs[mid]! <= x) low = mid;
      else high = mid;
    }
    const span = xs[high]! - xs[low]!;
    return { step: low, fraction: span < 1e-9 ? 0 : (x - xs[low]!) / span };
  }

  return {
    speed,
    range: xs[count - 1]!,
    heightAt(x: number): number {
      if (x <= 0) return zs[0]!;
      if (x >= xs[count - 1]!) return 0;
      const { step, fraction } = locate(x);
      return zs[step]! + (zs[step + 1]! - zs[step]!) * fraction;
    },
    timeAt(x: number): number {
      if (x <= 0) return 0;
      if (x >= xs[count - 1]!) return (count - 1) * TIME_STEP;
      const { step, fraction } = locate(x);
      return (step + fraction) * TIME_STEP;
    },
  };
}

/**
 * The chip that lands `range` metres away. Range grows with launch speed and nothing else moves,
 * so a plain bisection finds it; twenty halvings of a 34 m/s bracket settle it to well under a
 * millimetre a second.
 */
export function solveChip(range: number): ChipFlight {
  let low = MIN_SPEED;
  let high = MAX_SPEED;
  for (let i = 0; i < 20; i++) {
    const mid = (low + high) * 0.5;
    const count = fly(mid, _xs, _zs);
    if (_xs[count - 1]! < range) low = mid;
    else high = mid;
  }
  return chipFlight((low + high) * 0.5);
}

/**
 * The unit direction of a chip along `flat`, tilted up by the launch angle.
 *
 * Set the height on the raw offset to the target instead and it is divided by the target's own
 * distance, which is the mistake that kept every pass in this game on the floor for months. See
 * `autoPass`.
 */
export function chipDirection(flat: Vector3, fallback: Vector3, out = new Vector3()): Vector3 {
  out.copy(flat).setZ(0);
  if (out.lengthSq() < 1e-12) out.copy(fallback).setZ(0);
  if (out.lengthSq() < 1e-12) out.set(0, -1, 0);
  out.normalize().multiplyScalar(Math.cos(CHIP_ANGLE));
  out.z = Math.sin(CHIP_ANGLE);
  return out.normalize();
}
